if (!customElements.get('custom-scrollbar')) {
  customElements.define('custom-scrollbar', class CustomScrollbar extends HTMLElement {
    static instances = new Set();
    static resizeListenerAttached = false;
    static resizeRaf = null;
    static initObserverShared = null;
    static initObserverTargets = null;

    static handleGlobalResize() {
      if (CustomScrollbar.resizeRaf) return;
      CustomScrollbar.resizeRaf = requestAnimationFrame(() => {
        CustomScrollbar.resizeRaf = null;
        CustomScrollbar.instances.forEach((instance) => {
          if (!instance?.isConnected) return;
          instance._handleResize?.();
        });
      });
    }

    static attachResizeListener() {
      if (CustomScrollbar.resizeListenerAttached) return;
      window.addEventListener('resize', CustomScrollbar.handleGlobalResize);
      CustomScrollbar.resizeListenerAttached = true;
    }

    static detachResizeListenerIfIdle() {
      if (CustomScrollbar.instances.size > 0 || !CustomScrollbar.resizeListenerAttached) return;
      window.removeEventListener('resize', CustomScrollbar.handleGlobalResize);
      CustomScrollbar.resizeListenerAttached = false;
      if (CustomScrollbar.resizeRaf) {
        cancelAnimationFrame(CustomScrollbar.resizeRaf);
        CustomScrollbar.resizeRaf = null;
      }
    }

    static getInitObserver() {
      if (CustomScrollbar.initObserverShared) return CustomScrollbar.initObserverShared;

      CustomScrollbar.initObserverTargets = CustomScrollbar.initObserverTargets || new Map();
      CustomScrollbar.initObserverShared = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const instance = CustomScrollbar.initObserverTargets.get(entry.target);
          instance?.handleDeferredInit?.(entry);
        });
      }, { rootMargin: '200px 0px 200px 0px', threshold: 0 });

      return CustomScrollbar.initObserverShared;
    }

    static observeInit(instance) {
      if (!instance) return;
      CustomScrollbar.initObserverTargets = CustomScrollbar.initObserverTargets || new Map();
      CustomScrollbar.initObserverTargets.set(instance, instance);
      CustomScrollbar.getInitObserver().observe(instance);
    }

    static unobserveInit(instance) {
      if (!instance || !CustomScrollbar.initObserverShared) return;

      CustomScrollbar.initObserverShared.unobserve(instance);
      CustomScrollbar.initObserverTargets?.delete(instance);

      if (CustomScrollbar.initObserverTargets?.size === 0) {
        CustomScrollbar.initObserverShared.disconnect();
        CustomScrollbar.initObserverShared = null;
        CustomScrollbar.initObserverTargets = null;
      }
    }

    constructor() {
      super();
      this.state = {
        scrollContainer: null,
        scrollTrack: null,
        scrollThumb: null,
        isDragging: false,
        startX: 0,
        startY: 0,
        startScrollLeft: 0,
        startScrollTop: 0,
        thumbOffset: 0,
        isHorizontal: false,
        isDisabled: false,
        isFrozen: false
      };

      this.inertiaSettings = {
        scrollMultiplier: 0.2,
        friction: 0.5,
        stopThreshold: 0.5
      };

      this._handleMouseMove = this.handleMouseMove.bind(this);
      this._handleMouseUp = this.handleMouseUp.bind(this);
      this._handleScroll = () => this.updateThumbPosition();
      this._handleResize = this.handleResize.bind(this);
      this._handleWheel = this.handleWheel.bind(this);

      this._velocity = 0;
      this._rafId = null;
      this._updateRaf = null;
      this._measureDirty = true;
      this._freezeTimeout = null;
      this._metrics = {
        scrollWidth: 0,
        clientWidth: 0,
        scrollHeight: 0,
        clientHeight: 0,
        trackWidth: 0,
        trackHeight: 0,
      };
      this._thumbState = {
        width: '',
        height: '',
        transform: '',
        display: '',
      };
      this._visibilityObserver = null;
      this._isInitialized = false;
    }

    connectedCallback() {
      CustomScrollbar.instances.add(this);
      CustomScrollbar.attachResizeListener();
      this.setupDeferredInit();
    }

    setupDeferredInit() {
      if (this._isInitialized) return;

      if (!('IntersectionObserver' in window)) {
        this.handleDeferredInit();
        return;
      }

      CustomScrollbar.observeInit(this);
    }

    handleDeferredInit() {
      if (!this.isConnected || this._isInitialized) return;
      CustomScrollbar.unobserveInit(this);
      this.init();
    }

    init() {
      this.cleanup();

      this.state.scrollContainer = document.querySelector(this.getAttribute("target"));
      if (!this.state.scrollContainer) return;

      if (
        this.state.scrollContainer.clientWidth === 0 &&
        this.state.scrollContainer.clientHeight === 0
      ) {
        this._visibilityObserver = new ResizeObserver(() => {
          if (
            !this.state.scrollContainer ||
            (this.state.scrollContainer.clientWidth === 0 && this.state.scrollContainer.clientHeight === 0)
          ) return;
          this._visibilityObserver?.disconnect();
          this._visibilityObserver = null;
          this.init();
        });
        this._visibilityObserver.observe(this.state.scrollContainer);
        return;
      }

      const disableOnMobile = this.hasAttribute('disable-on-mobile') && window.innerWidth < 577;

      if (disableOnMobile) {
        this.state.isDisabled = true;
        this.classList.add('custom-scrollbar--disabled');
        return;
      }

      this.state.isDisabled = false;
      this._isInitialized = true;

      this.checkOrientation();
      this.setupScrollbar();
      this.addEventListeners();
      this.setupMutationObserver();

      this._resizeObserver = new ResizeObserver(() => {
        this.scheduleThumbUpdate(true);
      });
      this._resizeObserver.observe(this.state.scrollContainer);

      this.scheduleThumbUpdate(true);
    }

    checkOrientation() {
      const direction = this.getAttribute('data-direction');
      const mobileDirection = this.getAttribute('data-direction-mobile');

      if (direction === 'horizontal') {
        this.state.isHorizontal = true;
      } else if (direction === 'vertical' || !direction) {
        if (mobileDirection === 'horizontal' && window.innerWidth < 577) {
          this.state.isHorizontal = true;
        } else {
          this.state.isHorizontal = false;
        }
      }

      this.classList.toggle('custom-scrollbar--horizontal', this.state.isHorizontal);
      this.classList.toggle('custom-scrollbar--vertical', !this.state.isHorizontal);
    }

    handleResize() {
      const wasDisabled = this.state.isDisabled;
      const disableOnMobile = this.hasAttribute('disable-on-mobile') && window.innerWidth < 577;

      if (disableOnMobile && !wasDisabled) {
        this.cleanup();
        this.state.isDisabled = true;
        this.classList.add('custom-scrollbar--disabled');
        return;
      } else if (!disableOnMobile && wasDisabled) {
        this.state.isDisabled = false;
        this.classList.remove('custom-scrollbar--disabled');
        this.init();
        return;
      }

      if (this.state.isDisabled) return;

      const wasHorizontal = this.state.isHorizontal;
      this.checkOrientation();

      if (wasHorizontal !== this.state.isHorizontal) {
        this.scheduleThumbUpdate(true);
        if (this.state.isHorizontal) {
          this.scrollToCurrentItem();
        }
      }

      this.scheduleThumbUpdate(true);
    }

    hasScroll() {
      if (!this.state.scrollContainer) return false;
      const { scrollContainer } = this.state;
      return this.state.isHorizontal
        ? scrollContainer.scrollWidth > scrollContainer.clientWidth
        : scrollContainer.scrollHeight > scrollContainer.clientHeight;
    }

    setupScrollbar() {
      if (this.state.isDisabled) return;
      this.classList.add("custom-scrollbar");
      this.state.scrollTrack = this.querySelector(".custom-scrollbar__track");
      this.state.scrollThumb = this.querySelector(".custom-scrollbar__thumb");
      if (!this.state.scrollTrack || !this.state.scrollThumb) return;
    }

    cleanup() {
      const { scrollContainer } = this.state;
      if (scrollContainer) {
        scrollContainer.removeEventListener("scroll", this._handleScroll);
        scrollContainer.removeEventListener('wheel', this._handleWheel);
      }

      document.removeEventListener("mousemove", this._handleMouseMove);
      document.removeEventListener("mouseup", this._handleMouseUp);

      if (this._rafId) cancelAnimationFrame(this._rafId);
      if (this._updateRaf) cancelAnimationFrame(this._updateRaf);
      if (this._freezeTimeout) clearTimeout(this._freezeTimeout);
      this._observer?.disconnect();
      this._resizeObserver?.disconnect();
      this._isInitialized = false;
    }

    addEventListeners() {
      const { scrollContainer, scrollTrack, scrollThumb } = this.state;
      if (!scrollContainer) return;

      scrollContainer.addEventListener("scroll", this._handleScroll);

      if (this.state.isHorizontal) {
        scrollContainer.addEventListener('wheel', this._handleWheel, { passive: false });
      }

      scrollTrack?.addEventListener("click", (e) => this.handleTrackClick(e));
      scrollThumb?.addEventListener("mousedown", (e) => this.handleThumbMouseDown(e));
    }

    setupMutationObserver() {
      this._observer = new MutationObserver(() => {
        this.scheduleThumbUpdate(true);
      });
      this._observer.observe(this.state.scrollContainer, {
        childList: true,
        subtree: true,
      });
    }

    measureThumbMetrics() {
      const { scrollContainer, scrollTrack } = this.state;
      if (!scrollContainer || !scrollTrack) return;

      const nextMetrics = {
        scrollWidth: scrollContainer.scrollWidth,
        clientWidth: scrollContainer.clientWidth,
        scrollHeight: scrollContainer.scrollHeight,
        clientHeight: scrollContainer.clientHeight,
        trackWidth: scrollTrack.clientWidth,
        trackHeight: scrollTrack.clientHeight,
      };

      if (
        nextMetrics.clientWidth === 0 &&
        nextMetrics.clientHeight === 0 &&
        nextMetrics.trackWidth === 0 &&
        nextMetrics.trackHeight === 0
      ) {
        return;
      }

      this._metrics.scrollWidth = nextMetrics.scrollWidth;
      this._metrics.clientWidth = nextMetrics.clientWidth;
      this._metrics.scrollHeight = nextMetrics.scrollHeight;
      this._metrics.clientHeight = nextMetrics.clientHeight;
      this._metrics.trackWidth = nextMetrics.trackWidth;
      this._metrics.trackHeight = nextMetrics.trackHeight;
      this._measureDirty = false;
    }

    setThumbStyles(nextStyles = {}) {
      const { scrollThumb } = this.state;
      if (!scrollThumb) return;

      Object.entries(nextStyles).forEach(([key, value]) => {
        if (value == null) return;
        if (this._thumbState[key] === value) return;
        this._thumbState[key] = value;

        if (key === 'display') {
          scrollThumb.style.display = value;
        } else {
          scrollThumb.style[key] = value;
        }
      });
    }

    scheduleThumbUpdate(needsMeasure = false) {
      if (needsMeasure) this._measureDirty = true;
      if (this.state.isFrozen || this._updateRaf) return;

      this._updateRaf = requestAnimationFrame(() => {
        this._updateRaf = null;
        if (this._measureDirty) this.measureThumbMetrics();
        if (this.state.isHorizontal) this.updateHorizontalThumb();
        else this.updateVerticalThumb();
      });
    }

    handleWheel(e) {
      if (!this.state.isHorizontal) return;
      if (!this.hasScroll()) return;

      const dx = Math.abs(e.deltaX) < Math.abs(e.deltaY) ? e.deltaY : e.deltaX;
      const scrollAmount = dx * this.inertiaSettings.scrollMultiplier;

      this._velocity += scrollAmount;
      if (!this._rafId) this.applyInertia(this.state.scrollContainer);

      e.preventDefault();
    }

    applyInertia(scrollContainer) {
      if (Math.abs(this._velocity) < this.inertiaSettings.stopThreshold) {
        this._velocity = 0;
        this._rafId = null;
        return;
      }

      scrollContainer.scrollLeft += this._velocity;
      this.scheduleThumbUpdate();
      this._velocity *= this.inertiaSettings.friction;

      this._rafId = requestAnimationFrame(() => this.applyInertia(scrollContainer));
    }

    scrollToCurrentItem() {
      if (!this.state.isHorizontal) return;
      const item = this.state.scrollContainer?.querySelector('[aria-current="true"]');
      if (item) this.scrollToItem(item, true);
    }

    scrollToItem(item, instant = false, tabs = false, scrollPage = true) {
      if (!item || !this.hasScroll()) return;

      if (this._rafId) {
        cancelAnimationFrame(this._rafId);
        this._rafId = null;
      }
      this._velocity = 0;

      const { scrollContainer } = this.state;

      if (scrollPage) {
        item.scrollIntoView({
          behavior: instant ? 'auto' : 'smooth',
          block: 'nearest',
          inline: tabs ? 'center' : 'nearest'
        });
      } else {
        const itemRect = item.getBoundingClientRect();
        const containerRect = scrollContainer.getBoundingClientRect();

        if (this.state.isHorizontal) {
          if (itemRect.left < containerRect.left) {
            scrollContainer.scrollBy({
              left: itemRect.left - containerRect.left,
              behavior: instant ? 'auto' : 'smooth'
            });
          } else if (itemRect.right > containerRect.right) {
            scrollContainer.scrollBy({
              left: itemRect.right - containerRect.right,
              behavior: instant ? 'auto' : 'smooth'
            });
          }
        } else {
          if (itemRect.top < containerRect.top) {
            scrollContainer.scrollBy({
              top: itemRect.top - containerRect.top,
              behavior: instant ? 'auto' : 'smooth'
            });
          } else if (itemRect.bottom > containerRect.bottom) {
            scrollContainer.scrollBy({
              top: itemRect.bottom - containerRect.bottom,
              behavior: instant ? 'auto' : 'smooth'
            });
          }
        }
      }
    }

    handleTrackClick(e) {
      if (e.target !== this.state.scrollTrack) return;

      const { scrollTrack, scrollContainer } = this.state;
      const trackRect = scrollTrack.getBoundingClientRect();

      if (this.state.isHorizontal) {
        const scrollMax = scrollContainer.scrollWidth - scrollContainer.clientWidth;
        const targetScrollLeft = ((e.clientX - trackRect.left) / trackRect.width) * scrollMax;
        scrollContainer.scrollTo({ left: targetScrollLeft, behavior: 'smooth' });
      } else {
        const scrollableHeight = scrollContainer.scrollHeight - scrollContainer.clientHeight;
        const targetScrollTop = ((e.clientY - trackRect.top) / trackRect.height) * scrollableHeight;
        this.smoothScrollTo(scrollContainer, targetScrollTop, 300);
      }
    }

    smoothScrollTo(element, target, duration = 300) {
      const start = element.scrollTop;
      const change = target - start;
      const startTime = performance.now();

      const animateScroll = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);

        const ease = progress < 0.5
          ? 2 * progress * progress
          : -1 + (4 - 2 * progress) * progress;

        element.scrollTop = start + change * ease;

        if (elapsed < duration) {
          requestAnimationFrame(animateScroll);
        }
      };

      requestAnimationFrame(animateScroll);
    }

    handleThumbMouseDown(e) {
      this.state.isDragging = true;
      document.body.style.userSelect = "none";
      this.classList.add("custom-scrollbar--dragging");

      if (this._rafId) {
        cancelAnimationFrame(this._rafId);
        this._rafId = null;
      }
      this._velocity = 0;

      if (this.state.isHorizontal) {
        this.state.startX = e.clientX;
        this.state.startScrollLeft = this.state.scrollContainer.scrollLeft;
        const thumbRect = this.state.scrollThumb.getBoundingClientRect();
        this.state.thumbOffset = e.clientX - thumbRect.left;
      } else {
        this.state.startY = e.clientY;
        this.state.startScrollTop = this.state.scrollContainer.scrollTop;
        const thumbRect = this.state.scrollThumb.getBoundingClientRect();
        this.state.thumbOffset = e.clientY - thumbRect.top;
      }

      document.addEventListener("mousemove", this._handleMouseMove);
      document.addEventListener("mouseup", this._handleMouseUp);
      e.preventDefault();
    }

    handleMouseMove(e) {
      if (!this.state.isDragging) return;
      if (this.state.isHorizontal) this.handleHorizontalMouseMove(e);
      else this.handleVerticalMouseMove(e);
    }

    handleHorizontalMouseMove(e) {
      const { scrollTrack, scrollContainer, scrollThumb, thumbOffset } = this.state;
      const trackRect = scrollTrack.getBoundingClientRect();
      const thumbLeft = e.clientX - trackRect.left - thumbOffset;
      const trackWidth = scrollTrack.clientWidth;
      const thumbWidth = scrollThumb.clientWidth;
      const maxThumbLeft = trackWidth - thumbWidth;
      const clampedThumbLeft = Math.max(0, Math.min(thumbLeft, maxThumbLeft));
      scrollThumb.style.transform = `translateX(${clampedThumbLeft}px)`;
      const scrollMax = scrollContainer.scrollWidth - scrollContainer.clientWidth;
      const scrollRatio = maxThumbLeft > 0 ? clampedThumbLeft / maxThumbLeft : 0;
      const newScrollLeft = scrollRatio * scrollMax;
      scrollContainer.scrollLeft = newScrollLeft;
    }

    handleVerticalMouseMove(e) {
      const { scrollTrack, scrollContainer, scrollThumb, thumbOffset } = this.state;
      const trackRect = scrollTrack.getBoundingClientRect();
      const thumbTop = e.clientY - trackRect.top - thumbOffset;
      const trackHeight = scrollTrack.clientHeight;
      const thumbHeight = scrollThumb.clientHeight;
      const maxThumbTop = trackHeight - thumbHeight;
      const clampedThumbTop = Math.max(0, Math.min(thumbTop, maxThumbTop));
      scrollThumb.style.transform = `translateY(${clampedThumbTop}px)`;
      const scrollableHeight = scrollContainer.scrollHeight - scrollContainer.clientHeight;
      const scrollRatio = maxThumbTop > 0 ? clampedThumbTop / maxThumbTop : 0;
      const newScrollTop = scrollRatio * scrollableHeight;
      scrollContainer.scrollTop = newScrollTop;
    }

    handleMouseUp() {
      if (this.state.isDragging) {
        this.state.isDragging = false;
        document.body.style.userSelect = "";
        this.classList.remove("custom-scrollbar--dragging");
        document.removeEventListener("mousemove", this._handleMouseMove);
        document.removeEventListener("mouseup", this._handleMouseUp);
      }
    }

    freeze(delay = 0) {
      if (this.state.isDisabled || !this.state.scrollContainer) return;
      
      if (this._freezeTimeout) {
        clearTimeout(this._freezeTimeout);
      }
      
      this.state.isFrozen = true;
      
      this._freezeTimeout = setTimeout(() => {
        this.unfreeze();
      }, delay);
    }

    unfreeze() {
      if (!this.state.isFrozen) return;
      
      this.state.isFrozen = false;
      
      if (this._freezeTimeout) {
        clearTimeout(this._freezeTimeout);
        this._freezeTimeout = null;
      }
      
      requestAnimationFrame(() => {
        this.scheduleThumbUpdate();
      });
    }

    updateThumbPosition() {
      this.scheduleThumbUpdate();
    }

    updateHorizontalThumb() {
      const { scrollContainer, scrollTrack, scrollThumb } = this.state;
      if (!scrollContainer || !scrollTrack || !scrollThumb) return;
      const { scrollWidth, clientWidth, trackWidth } = this._metrics;
      const { scrollLeft } = scrollContainer;

      if (scrollWidth <= clientWidth) {
        this.setThumbStyles({ display: 'none' });
        this.classList.add('custom-scrollbar--no-scroll');
        this.classList.remove('custom-scrollbar--active', 'custom-scrollbar--start', 'custom-scrollbar--end');
        if (!this.state.isDragging) this.classList.remove('custom-scrollbar--dragging');
        return;
      }
      this.classList.remove('custom-scrollbar--no-scroll');
      this.classList.add('custom-scrollbar--active');
      const maxScroll = scrollWidth - clientWidth;
      this.classList.toggle('custom-scrollbar--start', scrollLeft === 0);
      this.classList.toggle('custom-scrollbar--end', scrollLeft >= maxScroll - 1);
      if (!this.state.isDragging && (scrollLeft === 0 || scrollLeft >= maxScroll - 1)) {
        this.classList.remove('custom-scrollbar--dragging');
      }
      const thumbW = Math.max((clientWidth / scrollWidth) * trackWidth, 30);
      if (!this.state.isDragging) {
        this.setThumbStyles({
          transform: `translateX(${(scrollLeft / maxScroll) * (trackWidth - thumbW)}px)`,
        });
      }
      this.setThumbStyles({
        width: `${thumbW}px`,
        height: '',
        display: 'block',
      });
    }

    updateVerticalThumb() {
      const { scrollContainer, scrollTrack, scrollThumb } = this.state;
      if (!scrollContainer || !scrollTrack || !scrollThumb) return;
      const { scrollHeight, clientHeight, trackHeight } = this._metrics;
      const { scrollTop } = scrollContainer;
      if (scrollHeight <= clientHeight + 1) {
        this.setThumbStyles({ display: 'none' });
        this.classList.remove("custom-scrollbar--start");
        if (!this.state.isDragging) this.classList.remove("custom-scrollbar--dragging");
        return;
      }
      this.setThumbStyles({ display: 'block' });
      this.classList.toggle("custom-scrollbar--start", scrollTop === 0);
      const scrollableHeight = scrollHeight - clientHeight;
      const thumbHeight = Math.max((clientHeight / scrollHeight) * trackHeight, 30);
      this.setThumbStyles({
        height: `${thumbHeight}px`,
        width: '',
      });
      if (!this.state.isDragging) {
        this.setThumbStyles({
          transform: `translateY(${(scrollTop / scrollableHeight) * (trackHeight - thumbHeight)}px)`,
        });
      }
      if (!this.state.isDragging && (scrollTop === 0 || scrollTop >= scrollableHeight - 1)) {
        this.classList.remove('custom-scrollbar--dragging');
      }
    }

    disconnectedCallback() {
      CustomScrollbar.instances.delete(this);
      CustomScrollbar.detachResizeListenerIfIdle();
      CustomScrollbar.unobserveInit(this);
      this._visibilityObserver?.disconnect();
      this._visibilityObserver = null;
      this.cleanup();
    }
  });
}

if (!customElements.get('horizontal-only-scrollbar')) {
  const OriginalScrollbar = customElements.get('custom-scrollbar');

  if (OriginalScrollbar) {
    customElements.define(
      'horizontal-only-scrollbar',
      class HorizontalOnlyScrollbar extends OriginalScrollbar {
        constructor() {
          super();

          this._targetScrollLeft = 0;
          this._rafId = null;

          this.settings = {
            smoothFactor: 0.3,
            wheelSpeed: 1.2,
            wheelEnabled: false
          };

          this.handleMouseMove = this.handleMouseMove.bind(this);
          this.handleMouseUp = this.handleMouseUp.bind(this);
          this.handleWheel = this.handleWheel.bind(this);
        }

        connectedCallback() {
          this.setAttribute('data-direction', 'horizontal');
          super.connectedCallback();
        }

        handleResize() {
          const wasDisabled = this.state.isDisabled;
          const disableOnMobile = this.hasAttribute('disable-on-mobile') &&
            window.innerWidth < 577;

          if (disableOnMobile && !wasDisabled) {
            this.cleanup();
            this.state.isDisabled = true;
            this.classList.add('custom-scrollbar--disabled');
            return;
          } else if (!disableOnMobile && wasDisabled) {
            this.state.isDisabled = false;
            this.classList.remove('custom-scrollbar--disabled');
            this.init();
            return;
          }

          if (this.state.isDisabled) return;
          this.updateHorizontalThumb();
        }

        addEventListeners() {
          const { scrollContainer, scrollTrack, scrollThumb } = this.state;
          if (!scrollContainer || !scrollTrack || !scrollThumb) return;

          scrollContainer.addEventListener("scroll", this._handleScroll);

          if (this.settings.wheelEnabled) {
            scrollContainer.addEventListener("wheel", this.handleWheel, { passive: false });
          }

          scrollTrack.addEventListener("click", (e) => this.handleTrackClick(e));
          scrollThumb.addEventListener("mousedown", (e) => this.handleThumbMouseDown(e));

          window.addEventListener("resize", this._handleResize);
        }

        reInit() {
          if (!this.state.scrollContainer) return;
          requestAnimationFrame(() => {
            this._targetScrollLeft = this.state.scrollContainer.scrollLeft;
            this.updateHorizontalThumb();
          });
        }

        animateScroll() {
          if (!this.state.scrollContainer) return;

          const current = this.state.scrollContainer.scrollLeft;
          const diff = this._targetScrollLeft - current;

          if (Math.abs(diff) <= 2) {
            this.state.scrollContainer.scrollLeft = this._targetScrollLeft;
            this._rafId = null;
            return;
          }

          this.state.scrollContainer.scrollLeft = current + diff * this.settings.smoothFactor;
          this._rafId = requestAnimationFrame(() => this.animateScroll());
        }

        handleWheel(e) {
          if (!this.settings.wheelEnabled || !this.state.scrollContainer) return;

          this._targetScrollLeft += e.deltaY * this.settings.wheelSpeed;

          const maxScroll = this.state.scrollContainer.scrollWidth - this.state.scrollContainer.clientWidth;
          this._targetScrollLeft = Math.max(0, Math.min(this._targetScrollLeft, maxScroll));

          if (!this._rafId) this.animateScroll();

          e.preventDefault();
        }

        setThumbPosition(newLeft) {
          const { scrollTrack, scrollContainer, scrollThumb } = this.state;
          const trackW = scrollTrack.clientWidth;
          const thumbW = scrollThumb.clientWidth;
          const clamped = Math.max(0, Math.min(newLeft, trackW - thumbW));
          const percent = trackW > thumbW ? clamped / (trackW - thumbW) : 0;

          scrollThumb.style.left = clamped + 'px';
          scrollThumb.style.transform = '';

          this._targetScrollLeft = percent * (scrollContainer.scrollWidth - scrollContainer.clientWidth);
          if (!this._rafId) this.animateScroll();
        }

        handleTrackClick(e) {
          if (e.target !== this.state.scrollTrack) return;
          const rect = this.state.scrollTrack.getBoundingClientRect();
          const clickX = e.clientX - rect.left;
          this.setThumbPosition(clickX - this.state.scrollThumb.clientWidth / 2);
        }

        handleThumbMouseDown(e) {
          this.state.isDragging = true;
          document.body.style.userSelect = "none";
          this.classList.add("custom-scrollbar--dragging");

          const thumbRect = this.state.scrollThumb.getBoundingClientRect();
          this.state.thumbOffset = e.clientX - thumbRect.left;

          document.addEventListener("mousemove", this.handleMouseMove);
          document.addEventListener("mouseup", this.handleMouseUp);
          e.preventDefault();
        }

        handleMouseMove(e) {
          if (!this.state.isDragging) return;
          const rect = this.state.scrollTrack.getBoundingClientRect();
          this.setThumbPosition(e.clientX - rect.left - this.state.thumbOffset);
        }

        handleMouseUp() {
          if (!this.state.isDragging) return;
          this.state.isDragging = false;
          document.body.style.userSelect = "";
          this.classList.remove("custom-scrollbar--dragging");
          document.removeEventListener("mousemove", this.handleMouseMove);
          document.removeEventListener("mouseup", this.handleMouseUp);
        }

        updateHorizontalThumb() {
          if (this.state.isDragging) return;
          const { scrollContainer, scrollTrack, scrollThumb } = this.state;
          if (!scrollContainer || !scrollTrack || !scrollThumb) return;

          const { scrollWidth, clientWidth, scrollLeft } = scrollContainer;
          const maxScroll = scrollWidth - clientWidth;

          if (maxScroll <= 0) {
            scrollThumb.style.display = 'none';
            this.classList.add('custom-scrollbar--no-scroll');
            this.classList.remove('custom-scrollbar--active', 'custom-scrollbar--start', 'custom-scrollbar--end');
            return;
          } else {
            scrollThumb.style.display = '';
            this.classList.remove('custom-scrollbar--no-scroll');
          }

          this.classList.add('custom-scrollbar--active');
          this.classList.toggle('custom-scrollbar--start', scrollLeft === 0);
          this.classList.toggle('custom-scrollbar--end', scrollLeft >= maxScroll - 1);

          const trackW = scrollTrack.clientWidth;
          const thumbW = Math.max((clientWidth / scrollWidth) * trackW, 30);
          const percent = scrollLeft / maxScroll;
          const thumbLeft = percent * (trackW - thumbW);

          scrollThumb.style.display = 'block';
          scrollThumb.style.width = thumbW + 'px';
          scrollThumb.style.left = thumbLeft + 'px';
          scrollThumb.style.transform = '';
          scrollThumb.style.height = '';
        }
      }
    );
  }
}
